import { Link, useParams } from "react-router-dom";
import { getActorById, getDramaById } from "@/utils/content";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import DramaCard from "@/components/feature/DramaCard";

export default function Actor() {
  const { id = "" } = useParams();
  const actor = getActorById(id);

  if (!actor) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
          <i className="ri-user-line text-3xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">未找到该演员资料</h1>
        <Link
          to="/"
          className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const works = actor.works
    .map((wid) => getDramaById(wid))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <title>{`${actor.name} - 演员资料与代表作品 - 善良的嫂子影视站`}</title>
      <meta
        name="description"
        content={`演员${actor.name}的个人资料、代表作品与角色介绍。${actor.bio.slice(0, 60)}`}
      />
      <meta name="keywords" content={`${actor.name},演员资料,代表作品,善良的嫂子,演员介绍`} />

      <Breadcrumbs items={[{ label: "演员资料" }, { label: actor.name }]} />

      <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="h-40 w-full md:h-56">
          <img
            src={actor.image}
            alt={`${actor.name} 写真`}
            title={`${actor.name} 演员资料`}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col gap-5 p-4 md:flex-row md:p-6">
          <div className="-mt-16 h-28 w-28 shrink-0 overflow-hidden rounded-lg border-4 border-background-50 bg-background-200 md:-mt-20 md:h-36 md:w-36">
            <img
              src={actor.avatar}
              alt={`${actor.name} 头像`}
              title={`${actor.name} 演员资料`}
              className="h-full w-full object-top"
            />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{actor.name}</h1>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span>出生日期：{actor.birthday}</span>
              <span>出生地：{actor.birthPlace}</span>
              <span>身高：{actor.height}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{actor.bio}</p>
          </div>
        </div>
      </section>

      {actor.roles.length > 0 && (
        <section className="mt-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">角色介绍</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {actor.roles.map((role) => {
              const drama = getDramaById(role.dramaId);
              return (
                <div key={role.dramaId} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-foreground-900">
                      《{drama ? drama.title : role.dramaId}》饰 {role.role}
                    </h3>
                    {drama && (
                      <Link
                        to={`/detail/${drama.id}`}
                        className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                      >
                        查看剧集
                        <span className="flex h-3.5 w-3.5 items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                      </Link>
                    )}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{role.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="mt-8">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">代表作品</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {works.map((drama) => (
            <DramaCard key={drama.id} drama={drama} />
          ))}
        </div>
      </section>
    </div>
  );
}